<template>
  <form class="space-y-6" @submit="submit">
    <FormField v-slot="{ componentField }" name="campaignId">
      <FormItem>
        <FormLabel
          class="mb-2 inline-block font-Aeonik text-[15px] font-bold uppercase leading-[18px]"
        >
          Choose a campaign
        </FormLabel>
        <FormControl>
          <CampaignsSelect v-bind="componentField" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="flex flex-col gap-2">
      <span class="font-Aeonik text-[15px] font-bold uppercase leading-[18px]">
        Choose your donation amount
      </span>

      <Box class="bg-white/80 p-3 @container">
        <div class="flex items-stretch gap-2">
          <div class="hidden flex-col justify-between @sm:flex">
            <div
              :style="{
                '--campaign-color': values.campaignId
                  ? colors[values.campaignId]
                  : undefined,
              }"
              class="relative flex h-[100px] w-[100px] items-center justify-center rounded-md border border-navy bg-[--campaign-color]"
            >
              <img
                class="h-[70px] w-[70px]"
                :src="values.campaignId ? emojis[values.campaignId] : undefined"
                alt="Impact"
              />
              <span
                v-if="campaign"
                class="shadowed-text absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-Kontesa text-[24px] font-extrabold text-white"
              >
                X{{
                  impactFormatter.format(
                    values.amount! *
                      campaign.dollarRatio *
                      (self?.type === "partner" ? 0.9 : 1),
                  )
                }}
              </span>
            </div>
            <button
              type="button"
              class="hidden w-full rounded-full border border-navy bg-white p-1.5 font-Aeonik text-[14px] font-bold leading-none shadow-[0_2px_0_0] shadow-navy transition-colors hover:bg-navy hover:text-white @sm:block"
              @click="playVideo"
            >
              Play video
            </button>
          </div>

          <div
            :style="{
              '--campaign-color': values.campaignId
                ? colors[values.campaignId]
                : undefined,
            }"
            class="min-w-0 grow rounded-md border border-navy bg-[--campaign-color]"
          >
            <div class="p-5 text-4xl @sm:hidden">
              <img
                class="inline-block h-10 w-10 align-middle"
                :src="values.campaignId ? emojis[values.campaignId] : undefined"
                alt="Impact"
              />

              <span
                v-if="campaign"
                class="shadowed-text align-middle font-Kontesa font-extrabold text-white"
              >
                X{{
                  impactFormatter.format(
                    values.amount! *
                      campaign.dollarRatio *
                      (self?.type === "partner" ? 0.9 : 1),
                  )
                }}
              </span>
            </div>

            <div class="-m-px rounded-md border border-navy p-5 @sm:p-3">
              <div
                class="mb-3 flex items-start justify-between gap-2 @sm:hidden"
              >
                <span
                  class="font-Aeonik text-[15px] font-bold uppercase leading-[21px] tracking-tight"
                >
                  {{ impactUnit }}
                  <template v-if="values.type === 'subscription'">
                    / month
                  </template>
                </span>

                <CampaignSummaryTooltipTrigger
                  class="shrink-0"
                  :campaign-id="values.campaignId!"
                />
              </div>

              <FormField v-slot="{ componentField }" name="amount">
                <FormItem>
                  <FormLabel class="sr-only">
                    Choose your donation amount
                  </FormLabel>
                  <FormControl>
                    <ProfileDonateMoreSlider
                      v-bind="componentField"
                      label="Amount"
                      :campaign-id="values.campaignId!"
                      :format-value="(value) => `$${value}`"
                      :min="1"
                      :max="30"
                      :step="1"
                    />

                    <Box
                      class="mt-2 flex items-center rounded-md bg-white outline-1 outline-offset-0 focus-within:outline"
                      rounded="unset"
                    >
                      <UiCurrencyInput
                        class="min-w-0 grow rounded-md px-2 py-0.5 font-Kontesa text-[20px] font-bold leading-none text-navy outline-none autofill:bg-transparent autofill:text-navy"
                        v-bind="componentField"
                      />
                    </Box>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <div class="flex justify-center p-5 @sm:p-1">
              <FormField v-slot="{ componentField }" name="type">
                <FormItem>
                  <FormLabel class="sr-only">Donation type</FormLabel>
                  <FormControl>
                    <RadioGroupRoot
                      v-bind="componentField"
                      orientation="horizontal"
                    >
                      <RadioGroupItem
                        class="px-1 text-[14px] font-bold leading-[24px] transition-colors data-[state=unchecked]:text-navy/40"
                        value="subscription"
                      >
                        Monthly
                      </RadioGroupItem>
                      <span class="text-[14px] text-navy/40">/</span>
                      <RadioGroupItem
                        class="px-1 text-[14px] font-bold leading-[24px] transition-colors data-[state=unchecked]:text-navy/40"
                        value="one-time"
                      >
                        One time
                      </RadioGroupItem>
                    </RadioGroupRoot>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="mt-3 w-full rounded-full border border-navy bg-white p-2 font-Aeonik text-[16px] font-bold leading-none shadow-[0_2px_0_0] shadow-navy transition-colors hover:bg-navy hover:text-white @sm:hidden"
          @click="playVideo"
        >
          Play video
        </button>
      </Box>
      <VideoPlayerModal v-if="videoId" :id="videoId" ref="video" />
    </div>

    <FormField
      v-if="self?.type !== 'partner'"
      v-slot="{ componentField }"
      name="tip"
    >
      <FormItem>
        <FormLabel
          class="mb-2 inline-block font-Aeonik text-[15px] font-bold uppercase leading-[18px]"
        >
          Do you wanna tip us?
        </FormLabel>
        <FormControl>
          <Box class="bg-white/80 p-3">
            <ProfileDonateMoreSlider
              v-bind="componentField"
              label="Tip"
              :campaign-id="values.campaignId!"
              :format-value="currencyFormatter.format"
              :min="0"
              :max="10"
              :step="0.5"
            />

            <Box
              class="mt-2 flex items-center rounded-md bg-white outline-1 outline-offset-0 focus-within:outline"
              rounded="unset"
            >
              <UiCurrencyInput
                class="min-w-0 grow rounded-md px-2 py-0.5 font-Kontesa text-[20px] font-bold leading-none text-navy outline-none autofill:bg-transparent autofill:text-navy"
                v-bind="componentField"
              />
            </Box>
          </Box>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <Separator
      class="my-6 border-b border-dashed border-navy"
      orientation="horizontal"
    />

    <Button variant="primary-flat" class="w-full" type="submit">Next</Button>
  </form>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as z from "zod";
import { Campaign, colors, emojis } from "~/lib/campaigns";
import {
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormMessage,
} from "~/components/Form";
import { useCampaign } from "~/domains/campaigns/composables";
import VideoPlayerModal from "~/components/VideoPlayer/Modal.vue";
import { useSelf } from "~/domains/member/composables";

const props = defineProps<{
  defaultValues: {
    campaignId: Campaign;
    amount: number;
    type: "subscription" | "one-time";
    tip: number;
  };
}>();

const { data: self } = useSelf();

const schema = z.object({
  campaignId: z.nativeEnum(Campaign),
  amount: z.coerce.number().min(0.01, "Donation must be at least $0.01"),
  type: z.enum(["one-time", "subscription"]),
  tip: z.coerce.number(),
});

const formSchema = toTypedSchema(schema);

const emit = defineEmits<{
  (e: "next", data: z.output<typeof schema>): void;
}>();

const { handleSubmit, values } = useForm({
  validationSchema: formSchema,
  initialValues: props.defaultValues,
});

const submit = handleSubmit((data) => {
  emit("next", data);
});

const { campaign } = useCampaign(() => values.campaignId!);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const impactFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 2,
});

const impactUnit = computed(() => {
  switch (values.campaignId) {
    case Campaign.TFTF:
      return "trees planted";
    case Campaign.OVI:
    case Campaign.PF:
    case Campaign.CLEANHUB:
      return "lbs of plastic removed";
    case Campaign.SARTHAK:
      return "days of school";
    case Campaign.REEFGEN:
      return "seagrass seeds planted";
    default:
      return "";
  }
});

const breakpoints = useBreakpoints({
  sm: useTheme().screens.sm,
});

const isMobile = computed(() => breakpoints.smaller("sm").value);
const videoId = computed(() =>
  isMobile.value
    ? campaign.value?.shortVideoIdMobile
    : campaign.value?.shortVideoId,
);
const video = ref<InstanceType<typeof VideoPlayerModal> | null>(null);

function playVideo() {
  video.value?.play();
}
</script>

<style scoped>
.shadowed-text {
  text-shadow:
    1px 4px rgb(var(--color-navy)),
    -1px 4px rgb(var(--color-navy));
  -webkit-text-stroke-width: 2px;
  -webkit-text-stroke-color: rgb(var(--color-navy));

  @apply -tracking-[0.05em];
}
</style>
